import { useState, useMemo, useRef, useEffect, useLayoutEffect, Fragment } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { taskStatus, TASK_STATUS_COLORS } from "@/lib/taskStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import CrewSpendCard from "@/components/CrewSpendCard";
import BookedRevenueCard from "@/components/BookedRevenueCard";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ChevronDown, Plus, CloudRain, Loader2, Trash2, Hammer, X, Link2 } from "lucide-react";

const DAY_W = 40;
const ROW_H = 36;
const NDAYS = 21;
const LEFT_W = 648;
const LEFT_COLS = "26px 56px 238px 86px 40px 60px 60px 82px";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parse = (s) => new Date(`${s.slice(0, 10)}T00:00:00`);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtD = (s) => (s ? parse(s).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : "—");
const initials = (n) => (n || "").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const isWork = (d, blocked) => d.getDay() !== 0 && d.getDay() !== 6 && !blocked.has(ymd(d));
const nextWork = (d, blocked) => { let x = new Date(d), g = 0; while (!isWork(x, blocked) && g < 400) { x = addDays(x, 1); g++; } return x; };
const addWork = (d, n, blocked) => { let x = new Date(d), g = 0; while (n > 0 && g < 800) { x = addDays(x, 1); if (isWork(x, blocked)) n--; g++; } return x; };
const wdSpan = (s, e) => {
  if (!s || !e) return "—";
  let d = parse(s), n = 0, g = 0;
  const end = parse(e);
  while (d <= end && g < 220) { if (d.getDay() !== 0 && d.getDay() !== 6) n++; d = addDays(d, 1); g++; }
  return n === 0 ? "—" : `${n}d`;
};

const CrewChips = ({ names }) => (
  <span className="flex gap-1 overflow-hidden">
    {(names || []).slice(0, 3).map((n) => (
      <span key={n} title={n} className="bg-sky-100 text-sky-800 text-[9.5px] font-bold rounded-full px-1.5 py-0.5 shrink-0">{initials(n)}</span>
    ))}
    {(names || []).length > 3 && <span className="text-[9.5px] text-slate-400">+{names.length - 3}</span>}
  </span>
);

export default function ProjectView({ focusTaskId = null, onFocusHandled, defaultExpand = null, onExpandChange }) {
  const nav = useNavigate();
  const [win0, setWin0] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d;
  });
  const days = useMemo(() => Array.from({ length: NDAYS }, (_, i) => {
    const d = addDays(win0, i);
    return { d, key: ymd(d), we: d.getDay() === 0 || d.getDay() === 6, today: ymd(d) === ymd(new Date()) };
  }), [win0]);

  const { data: plan, isLoading } = useQuery({
    queryKey: ["project-plan"],
    queryFn: async () => (await api.get("/project/plan")).data,
  });
  const { data: team = [] } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/auth/team")).data });
  const canEdit = !!plan?.can_edit;
  const jobs = useMemo(() => plan?.jobs || [], [plan]);

  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    if (!plan) return;
    setExpanded((cur) => {
      const next = { ...cur };
      for (const j of plan.jobs) if (!(j.id in next)) next[j.id] = defaultExpand ? defaultExpand === "expand" : j.tasks.length > 0;
      return next;
    });
  }, [plan]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply the saved expand/collapse default (it may arrive after the plan did).
  const appliedExpandRef = useRef(null);
  useEffect(() => {
    if (!plan || !defaultExpand || appliedExpandRef.current === defaultExpand) return;
    appliedExpandRef.current = defaultExpand;
    setExpanded(Object.fromEntries(plan.jobs.map((j) => [j.id, defaultExpand === "expand"])));
  }, [plan, defaultExpand]);

  const [crewFilter, setCrewFilter] = useState("all");
  const visibleJobs = useMemo(() => jobs.filter((j) =>
    crewFilter === "all" || (j.crew || []).includes(crewFilter) || j.tasks.some((t) => (t.crew || []).includes(crewFilter))
  ), [jobs, crewFilter]);

  const [taskDraft, setTaskDraft] = useState(null);   // {jobId, task|null}
  const [delayDraft, setDelayDraft] = useState(null); // {jobId, delay|null}

  // Scheduler crew-task chip → expand the job, scroll the window to the task's
  // week and open its edit dialog.
  useEffect(() => {
    if (!focusTaskId || !plan) return;
    const job = plan.jobs.find((j) => j.tasks.some((t) => t.id === focusTaskId));
    if (job) {
      const task = job.tasks.find((t) => t.id === focusTaskId);
      setExpanded((c) => ({ ...c, [job.id]: true }));
      if (task.start) {
        const d = parse(task.start);
        d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
        setWin0(d);
      }
      setTaskDraft({ jobId: job.id, task });
    }
    onFocusHandled?.();
  }, [focusTaskId, plan]); // eslint-disable-line react-hooks/exhaustive-deps

  // Day columns stretch to fill wide screens (Iter-84 polish).
  const wrapRef = useRef(null);
  const [dayW, setDayW] = useState(DAY_W);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const fit = () => setDayW(Math.max(DAY_W, Math.floor((el.clientWidth - LEFT_W - 2) / NDAYS)));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isLoading]);

  const seg2pos = (s, e) => {
    const a = Math.round((parse(s) - win0) / 86400000);
    const b = Math.round((parse(e) - win0) / 86400000);
    if (b < 0 || a > NDAYS - 1) return null;
    const a2 = Math.max(0, a), b2 = Math.min(NDAYS - 1, b);
    return { left: a2 * dayW + 2, width: (b2 - a2 + 1) * dayW - 4 };
  };

  // Dependency arrows — measured off the rendered bars after paint.
  const sheetRef = useRef(null);
  const barRefs = useRef({});
  const [arrows, setArrows] = useState([]);
  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => {
      const sheet = sheetRef.current;
      if (!sheet) { setArrows([]); return; }
      const S = sheet.getBoundingClientRect();
      const out = [];
      const predJobExpanded = (p) => expanded[p.pred_job_id];
      for (const j of visibleJobs) {
        if (!expanded[j.id]) continue;
        for (const t of j.tasks) {
          for (const p of (t.predecessors || [])) {
            const live = (el) => (el && el.isConnected ? el : null);
            const link = p.link_type || "FS";
            const isSS = link === "SS";
            const isFF = link === "FF";
            const from = (predJobExpanded(p) ? live(barRefs.current[`${p.task_id}|${isSS ? "first" : "last"}`]) : null)
              || live(barRefs.current[`jobsum:${p.pred_job_id}`]);
            const to = barRefs.current[`${t.id}|${isFF ? "last" : "first"}`];
            if (!from || !to || !from.isConnected || !to.isConnected) continue;
            const A = from.getBoundingClientRect(), B = to.getBoundingClientRect();
            const y1 = A.top - S.top + A.height / 2;
            const y2 = B.top - S.top + B.height / 2;
            let d, head;
            if (isSS) {
              // Start→Start: leave the pred bar's LEFT edge, drop, enter the successor's start.
              const x1 = A.left - S.left;
              const x2 = B.left - S.left;
              d = `M${x1},${y1} H${Math.min(x1 - 5, x2 - 8)} V${y2} H${x2 - 5}`;
              head = `M${x2 - 5},${y2 - 3.5} L${x2 - 1},${y2} L${x2 - 5},${y2 + 3.5} Z`;
            } else if (isFF) {
              // Finish→Finish: leave the pred bar's RIGHT edge, enter the successor's END.
              const x1 = A.right - S.left;
              const x2 = B.right - S.left;
              d = `M${x1},${y1} H${Math.max(x1 + 5, x2 + 8)} V${y2} H${x2 + 5}`;
              head = `M${x2 + 5},${y2 - 3.5} L${x2 + 1},${y2} L${x2 + 5},${y2 + 3.5} Z`;
            } else {
              const x1 = A.right - S.left;
              const x2 = B.left - S.left;
              d = `M${x1},${y1} H${x1 + 5} V${y2} H${Math.max(x2 - 5, x1 + 5)}`;
              head = `M${x2 - 5},${y2 - 3.5} L${x2 - 1},${y2} L${x2 - 5},${y2 + 3.5} Z`;
            }
            out.push({
              key: `${t.id}:${p.task_id}`,
              cross: !!p.cross,
              d,
              head,
            });
          }
        }
      }
      setArrows(out);
    });
    return () => cancelAnimationFrame(id);
  }, [plan, expanded, win0, crewFilter, visibleJobs, dayW]);

  const winEnd = addDays(win0, NDAYS - 1);
  const setAll = (v) => {
    setExpanded(Object.fromEntries(jobs.map((j) => [j.id, v])));
    appliedExpandRef.current = v ? "expand" : "collapse";
    onExpandChange?.(v ? "expand" : "collapse");
  };
  const crewNames = useMemo(() => [...new Set([...jobs.flatMap((j) => j.crew || []), ...jobs.flatMap((j) => j.tasks.flatMap((t) => t.crew || []))])].sort(), [jobs]);

  if (isLoading) {
    return <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center" data-testid="project-loading"><Loader2 size={16} className="animate-spin" /> Loading project plan…</div>;
  }

  return (
    <div className="space-y-3" data-testid="project-view">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" className="rounded-sm h-9 w-9 p-0" onClick={() => setWin0((d) => addDays(d, -7))} data-testid="project-prev"><ChevronLeft size={15} /></Button>
        <div className="font-display font-semibold text-sm text-slate-900 min-w-[150px] text-center" data-testid="project-range">
          {win0.toLocaleDateString("en-AU", { day: "numeric", month: "short" })} — {winEnd.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
        </div>
        <Button variant="outline" size="sm" className="rounded-sm h-9 w-9 p-0" onClick={() => setWin0((d) => addDays(d, 7))} data-testid="project-next"><ChevronRight size={15} /></Button>
        <Button variant="ghost" size="sm" className="rounded-sm h-9 text-cyan-600" onClick={() => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); setWin0(d); }} data-testid="project-today">Today</Button>
        <Button variant="outline" size="sm" className="rounded-sm h-9 text-xs" onClick={() => setAll(true)} data-testid="project-expand-all">Expand all</Button>
        <Button variant="outline" size="sm" className="rounded-sm h-9 text-xs" onClick={() => setAll(false)} data-testid="project-collapse-all">Collapse all</Button>
        <Select value={crewFilter} onValueChange={setCrewFilter}>
          <SelectTrigger className="rounded-sm h-9 w-[150px] text-xs" data-testid="project-crew-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Crew: everyone</SelectItem>
            {crewNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        {!canEdit && <Badge variant="outline" className="rounded-sm text-[10px] border-slate-300 text-slate-500">VIEW ONLY</Badge>}
        <div className="ml-auto flex flex-wrap items-center gap-3 text-[10.5px] text-slate-500" data-testid="project-legend">
          <span className="flex items-center gap-1"><span className="inline-block w-3.5 h-2 rounded-[2px] bg-[#1e4060]" /> Job</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3.5 h-2.5 rounded-[2px] bg-[#8ce0b4]" /> Completed</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3.5 h-2.5 rounded-[2px] bg-[#fcd34d]" /> In progress</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3.5 h-2.5 rounded-[2px] bg-[#7fb3dd]" /> Future</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3.5 h-2.5 rounded-[2px] bg-[#fda4af]" /> Overdue</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3.5 h-2.5 rounded-[2px]" style={{ background: "linear-gradient(90deg,#2f6ea5 0 50%,#7fb3dd 50% 100%)" }} /> % done fill</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3.5 h-2.5 rounded-[2px]" style={{ background: "repeating-linear-gradient(45deg,#fcd34d 0 4px,#f8b84e 4px 8px)" }} /> Delay</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3.5 h-2.5 rounded-[2px] bg-slate-300/50" /> Weekend</span>
        </div>
      </div>

      {/* Sheet */}
      <div ref={wrapRef} className="bg-white border border-slate-200 rounded-sm industrial-shadow overflow-x-auto">
        <div ref={sheetRef} className="relative grid" style={{ gridTemplateColumns: `${LEFT_COLS} repeat(${NDAYS}, ${dayW}px)`, minWidth: LEFT_W + NDAYS * dayW }}>
          {/* header */}
          {["", "ID", "Task name", "Crew", "Dur", "Start", "Finish", "Pred"].map((h, i) => (
            <div key={i} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-2 py-2 bg-slate-50 border-b-2 border-slate-200 whitespace-nowrap">{h}</div>
          ))}
          {days.map((x) => (
            <div key={x.key} className={`text-[9.5px] font-bold text-center py-1.5 border-b-2 border-slate-200 border-l border-slate-100 leading-tight ${x.today ? "bg-cyan-50 text-cyan-700" : x.we ? "bg-slate-100 text-slate-400" : "bg-slate-50 text-slate-600"}`}>
              {x.d.toLocaleDateString("en-AU", { weekday: "short" })}<br />
              <span className="font-medium text-slate-400">{x.d.getDate()}{x.d.getDate() === 1 || x.key === days[0].key ? ` ${x.d.toLocaleDateString("en-AU", { month: "short" })}` : ""}</span>
            </div>
          ))}

          {visibleJobs.map((j) => {
            const open = !!expanded[j.id];
            const gridId = (s) => (s || "").replace(/^[A-Za-z]+-/, "");
            const rows = [
              ...j.tasks.map((t) => ({ kind: "task", ...t })),
              ...j.delays.map((dl) => ({ kind: "delay", ...dl, rstart: dl.dates[0] || dl.start, rfinish: dl.dates[dl.dates.length - 1] || dl.start })),
            ].sort((a, b) => ((a.kind === "task" ? a.start : a.rstart) || "").localeCompare((b.kind === "task" ? b.start : b.rstart) || ""));
            const sumPos = j.start && j.finish ? seg2pos(j.start, j.finish) : null;
            return (
              <Fragment key={j.id}>
                {/* job row */}
                <div className="flex items-center justify-center bg-slate-50 border-b border-slate-200">
                  <button onClick={() => setExpanded((c) => ({ ...c, [j.id]: !open }))} className="text-cyan-700 p-1" data-testid={`project-expand-${j.id}`}>
                    {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  </button>
                </div>
                <button onClick={() => nav(`/jobs/${j.id}`)} title={`${j.job_number || "Job"} — open install job page`}
                  className="flex items-center bg-slate-50 border-b border-slate-200 px-1.5 text-[10px] font-mono font-bold text-cyan-700 hover:underline whitespace-nowrap overflow-hidden text-left"
                  data-testid={`project-job-id-link-${j.id}`}>{j.job_short || gridId(j.job_number)}</button>
                <div className="flex items-center gap-1.5 bg-slate-50 border-b border-slate-200 px-2 overflow-hidden" style={{ height: ROW_H }} data-testid={`project-job-row-${j.id}`}>
                  <Hammer size={11} className="text-emerald-600 shrink-0" />
                  <span className="text-xs font-bold text-slate-900 truncate">
                    {j.job_number && (
                      <button onClick={() => nav(`/jobs/${j.id}`)} title="Open install job page"
                        className="text-cyan-700 hover:underline" data-testid={`project-job-link-${j.id}`}>{j.job_number}</button>
                    )}
                    {j.job_number ? " — " : ""}{j.title}
                  </span>
                  {canEdit && (
                    <span className="ml-auto flex gap-0.5 shrink-0">
                      <button title="Add task" onClick={() => { setExpanded((c) => ({ ...c, [j.id]: true })); setTaskDraft({ jobId: j.id, task: null }); }} className="text-cyan-600 hover:bg-cyan-50 rounded-sm p-0.5" data-testid={`project-add-task-${j.id}`}><Plus size={13} /></button>
                      <button title="Add delay" onClick={() => setDelayDraft({ jobId: j.id, delay: null })} className="text-amber-600 hover:bg-amber-50 rounded-sm p-0.5" data-testid={`project-add-delay-${j.id}`}><CloudRain size={13} /></button>
                    </span>
                  )}
                </div>
                <div className="flex items-center bg-slate-50 border-b border-slate-200 px-2" style={{ height: ROW_H }}><CrewChips names={j.crew} /></div>
                <div className="flex items-center bg-slate-50 border-b border-slate-200 px-1.5 text-[10.5px] font-mono text-slate-600">{wdSpan(j.start, j.finish)}</div>
                <div className="flex items-center bg-slate-50 border-b border-slate-200 px-1.5 text-[10.5px] font-mono text-slate-600 whitespace-nowrap">{fmtD(j.start)}</div>
                <div className="flex items-center bg-slate-50 border-b border-slate-200 px-1.5 text-[10.5px] font-mono text-slate-600 whitespace-nowrap">{fmtD(j.finish)}</div>
                <div className="bg-slate-50 border-b border-slate-200" />
                <Lane days={days} dayW={dayW} isJob>
                  {sumPos && <div ref={(el) => { if (el) barRefs.current[`jobsum:${j.id}`] = el; }} className="absolute rounded-[3px] bg-[#1e4060]" style={{ left: sumPos.left, width: sumPos.width, top: 12, height: 13 }} data-testid={`project-summary-bar-${j.id}`} />}
                  {!sumPos && !j.start && <span className="absolute left-2 top-2.5 text-[10px] text-slate-400 italic">unscheduled</span>}
                </Lane>

                {/* detail rows */}
                {open && rows.map((r) => r.kind === "task" ? (
                  <Fragment key={r.id}>
                    <div className="border-b border-slate-100" />
                    <div className="flex items-center px-1.5 text-[9.5px] font-mono text-slate-400 border-b border-slate-100 whitespace-nowrap overflow-hidden" title={r.code} data-testid={`project-task-id-${r.id}`}>{gridId(r.short_id)}</div>
                    <button onClick={() => setTaskDraft({ jobId: j.id, task: r })} className="flex items-center text-left px-2 pl-4 border-b border-slate-100 hover:bg-cyan-50/50 overflow-hidden" style={{ height: ROW_H }} data-testid={`project-task-row-${r.id}`}>
                      <span className={`text-xs truncate ${r.percent === 100 ? "text-slate-400 line-through" : "text-slate-700"}`}>{r.name}</span>
                    </button>
                    <div className="flex items-center px-2 border-b border-slate-100"><CrewChips names={r.crew} /></div>
                    <div className="flex items-center px-1.5 text-[10.5px] font-mono text-slate-500 border-b border-slate-100">{r.duration}d</div>
                    <div className="flex items-center px-1.5 text-[10.5px] font-mono text-slate-500 border-b border-slate-100 whitespace-nowrap">{fmtD(r.start)}</div>
                    <div className="flex items-center px-1.5 text-[10.5px] font-mono text-slate-500 border-b border-slate-100 whitespace-nowrap">{fmtD(r.finish)}</div>
                    <div className="flex items-center gap-1 px-1.5 text-[10px] border-b border-slate-100 whitespace-nowrap overflow-hidden"
                      title={(r.predecessors || []).map((p) => `${p.code} ${p.link_type}${p.lag ? `+${p.lag}` : ""} — ${p.name}`).join("\n")}>
                      {(r.predecessors || []).length === 0 ? <span className="text-slate-400">—</span> : r.predecessors.slice(0, 2).map((p, pi) => (
                        <span key={pi} className={p.cross ? "text-violet-600 font-semibold" : "text-slate-500"}>
                          {p.short} {p.link_type}{p.lag ? `+${p.lag}` : ""}{pi < Math.min(r.predecessors.length, 2) - 1 ? "," : ""}
                        </span>
                      ))}
                      {(r.predecessors || []).length > 2 && <span className="text-[9px] font-bold text-slate-500 bg-slate-100 rounded-full px-1">+{r.predecessors.length - 2}</span>}
                    </div>
                    <Lane days={days} dayW={dayW}>
                      {(() => {
                        // % complete fills working days from the left across split segments.
                        const filledDays = Math.round((r.duration || 1) * ((r.percent || 0) / 100));
                        const stc = TASK_STATUS_COLORS[taskStatus(r)];
                        const isLate = taskStatus(r) === "late";
                        let consumed = 0;
                        return r.segments.map((seg, si) => {
                          const pos = seg2pos(seg.start, seg.end);
                          const segLen = Math.round((parse(seg.end) - parse(seg.start)) / 86400000) + 1;
                          const fillHere = Math.max(0, Math.min(filledDays - consumed, segLen));
                          consumed += segLen;
                          if (!pos) return null;
                          // Part-day tasks: trim the bar by its start/end day fractions (¼-day scheduling).
                          let left = pos.left, width = pos.width;
                          const isFirst = si === 0, isLast = si === r.segments.length - 1;
                          if (isFirst && (r.start_frac || 0) > 0 && Math.round((parse(seg.start) - win0) / 86400000) >= 0) {
                            const off = r.start_frac * dayW;
                            left += off; width -= off;
                          }
                          if (isLast && r.end_frac != null && r.end_frac < 1 && Math.round((parse(seg.end) - win0) / 86400000) <= NDAYS - 1) {
                            width -= (1 - r.end_frac) * dayW;
                          }
                          width = Math.max(7, width);
                          const refKey = si === 0 ? `${r.id}|first` : null;
                          const lastKey = si === r.segments.length - 1 ? `${r.id}|last` : null;
                          return (
                            <button key={si}
                              ref={(el) => { if (el) { if (refKey) barRefs.current[refKey] = el; if (lastKey) barRefs.current[lastKey] = el; } }}
                              onClick={() => setTaskDraft({ jobId: j.id, task: r })}
                              className="absolute rounded-[4px] shadow-sm overflow-hidden hover:ring-2 hover:ring-cyan-400"
                              style={{ left, width, top: 7, height: 22, background: stc.base }}
                              data-testid={si === 0 ? `project-task-bar-${r.id}` : undefined}>
                              {fillHere > 0 && <span className="absolute left-0 top-0 bottom-0" style={{ width: `${(fillHere / segLen) * 100}%`, background: stc.fill }} />}
                              {isLate && si === r.segments.length - 1 && width > 60 && (
                                <span className="absolute right-1 top-[4px] text-[8px] font-extrabold tracking-wide text-white bg-rose-600/95 rounded-[2px] px-1" data-testid={`project-task-overdue-${r.id}`}>OVERDUE</span>
                              )}
                              {si === 0 && r.percent > 0 && r.percent < 100 && width > 34 && (
                                <span className="absolute left-1.5 top-[3px] text-[9px] font-bold text-white drop-shadow">{r.percent}%</span>
                              )}
                            </button>
                          );
                        });
                      })()}
                    </Lane>
                  </Fragment>
                ) : (
                  <Fragment key={r.id}>
                    <div className="border-b border-amber-100 bg-amber-50/40" />
                    <div className="border-b border-amber-100 bg-amber-50/40" />
                    <button onClick={() => setDelayDraft({ jobId: j.id, delay: r })} className="flex items-center gap-1.5 text-left px-2 pl-4 border-b border-amber-100 bg-amber-50/40 hover:bg-amber-50 overflow-hidden" style={{ height: ROW_H }} data-testid={`project-delay-row-${r.id}`}>
                      <CloudRain size={11} className="text-amber-600 shrink-0" />
                      <span className="text-xs text-amber-800 font-medium truncate">Delay — {r.reason}</span>
                    </button>
                    <div className="border-b border-amber-100 bg-amber-50/40" />
                    <div className="flex items-center px-1.5 text-[10.5px] font-mono text-amber-700 border-b border-amber-100 bg-amber-50/40">{r.days}d</div>
                    <div className="flex items-center px-1.5 text-[10.5px] font-mono text-amber-700 border-b border-amber-100 bg-amber-50/40 whitespace-nowrap">{fmtD(r.rstart)}</div>
                    <div className="flex items-center px-1.5 text-[10.5px] font-mono text-amber-700 border-b border-amber-100 bg-amber-50/40 whitespace-nowrap">{fmtD(r.rfinish)}</div>
                    <div className="border-b border-amber-100 bg-amber-50/40" />
                    <Lane days={days} dayW={dayW}>
                      {(() => {
                        const pos = seg2pos(r.rstart, r.rfinish);
                        return pos ? (
                          <button onClick={() => setDelayDraft({ jobId: j.id, delay: r })}
                            title={`${r.reason} — ${r.days}d`}
                            className="absolute rounded-[4px] border border-dashed border-amber-600 overflow-hidden"
                            style={{ left: pos.left, width: pos.width, top: 7, height: 22, background: "repeating-linear-gradient(45deg,#fcd34d 0 6px,#f8b84e 6px 12px)" }}
                            data-testid={`project-delay-bar-${r.id}`}>
                            {pos.width > 40 && (
                              <span className="absolute left-1.5 top-[3px] text-[9px] font-bold text-amber-900 whitespace-nowrap">
                                {pos.width > 120 ? `${r.reason} — ${r.days}d` : `${r.days}d`}
                              </span>
                            )}
                          </button>
                        ) : null;
                      })()}
                    </Lane>
                  </Fragment>
                ))}

                {open && rows.length === 0 && (
                  <Fragment>
                    <div className="border-b border-slate-100" />
                    <div className="border-b border-slate-100" />
                    <div className="flex items-center px-2 pl-4 border-b border-slate-100 text-[11px] text-slate-400 italic gap-2" style={{ height: ROW_H }} data-testid={`project-empty-${j.id}`}>
                      No task plan yet
                      {canEdit && (
                        <button onClick={() => setTaskDraft({ jobId: j.id, task: null })} className="text-cyan-600 font-semibold not-italic hover:underline" data-testid={`project-add-first-task-${j.id}`}>+ Add the first task</button>
                      )}
                    </div>
                    {[0, 1, 2, 3, 4].map((i) => <div key={i} className="border-b border-slate-100" />)}
                    <Lane days={days} dayW={dayW} />
                  </Fragment>
                )}
              </Fragment>
            );
          })}

          <svg className="absolute inset-0 pointer-events-none" style={{ overflow: "visible" }} data-testid="project-arrows">
            {arrows.map((a) => (
              <g key={a.key}>
                <path d={a.d} fill="none" stroke={a.cross ? "#7c3aed" : "#475569"} strokeWidth="1.3" strokeDasharray={a.cross ? "4 2" : undefined} />
                <path d={a.head} fill={a.cross ? "#7c3aed" : "#475569"} />
              </g>
            ))}
          </svg>
        </div>
        {visibleJobs.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="project-no-jobs">No active install jobs{crewFilter !== "all" ? " for this crew member" : ""}.</div>
        )}
      </div>

      <CrewSpendCard />
      <BookedRevenueCard />

      <div className="text-[11px] text-slate-500 bg-sky-50 border border-sky-200 rounded-sm px-3 py-2 leading-relaxed" data-testid="project-hint">
        Click any task, bar or delay to open it. Tasks chain Finish→Start, Start→Start or Finish→Finish with optional lag — including <span className="text-violet-600 font-semibold">across jobs</span> (dashed violet arrows; IDs like J-1018.3). Durations go down to ¼ day, so short dependent tasks can finish on the same day. Change one task and every dependent in every linked job reschedules automatically, skipping weekends and delays.
      </div>

      <TaskDialog
        draft={taskDraft} jobs={jobs} team={team} canEdit={canEdit}
        onClose={() => setTaskDraft(null)}
        onAddDelay={(jobId) => { setTaskDraft(null); setDelayDraft({ jobId, delay: null }); }}
      />
      <DelayDialog draft={delayDraft} jobs={jobs} canEdit={canEdit} onClose={() => setDelayDraft(null)} />
    </div>
  );
}

const Lane = ({ days, isJob, dayW, children }) => (
  <div className={`relative border-b ${isJob ? "bg-slate-50 border-slate-200" : "border-slate-100"}`}
    style={{ gridColumn: `9 / span ${days.length}`, height: ROW_H, backgroundImage: `repeating-linear-gradient(to right,#f1f5f9 0 1px,transparent 1px ${dayW}px)` }}>
    {days.map((x, i) => x.we ? <span key={i} className="absolute top-0 bottom-0 bg-slate-400/10 pointer-events-none" style={{ left: i * dayW, width: dayW }} /> : null)}
    {days.map((x, i) => x.today ? <span key={`t${i}`} className="absolute top-0 bottom-0 border-l-2 border-cyan-500/50 pointer-events-none" style={{ left: i * dayW }} /> : null)}
    {children}
  </div>
);

export function invalidateAll(qc) {
  qc.invalidateQueries({ queryKey: ["project-plan"] });
  qc.invalidateQueries({ queryKey: ["scheduler-events"] });
  qc.invalidateQueries({ queryKey: ["jobs"] });
  qc.invalidateQueries({ queryKey: ["job"] });
  qc.invalidateQueries({ queryKey: ["activities"] });
}

const TASK_PRESETS = [
  "Order materials", "Materials delivered", "Demolition works", "Post holes",
  "Concrete / Fix posts", "Install panels", "Install gates", "Site cleanup", "Final inspection",
];

const parsePresetName = (name) => {
  for (const p of TASK_PRESETS) {
    if (name === p) return { preset: p, detail: "" };
    if (name.startsWith(`${p} - `)) return { preset: p, detail: name.slice(p.length + 3) };
  }
  return { preset: name ? "custom" : "", detail: "" };
};

export function TaskDialog({ draft, jobs, team, canEdit, onClose, onAddDelay }) {
  const qc = useQueryClient();
  const job = jobs.find((j) => j.id === draft?.jobId);
  const t = draft?.task || null;
  const [f, setF] = useState(null);
  const { data: crewOptions = [] } = useQuery({
    queryKey: ["crew-options"],
    queryFn: async () => (await api.get("/project/crew-options")).data,
    enabled: !!draft,
    staleTime: 60000,
  });
  useEffect(() => {
    if (!draft) { setF(null); return; }
    const pn = parsePresetName(t?.name || "");
    setF({
      name: pn.preset === "custom" ? (t?.name || "") : "", name_preset: pn.preset, name_detail: pn.detail,
      duration: t?.duration || 1, percent: t?.percent || 0, crew: t?.crew || [],
      billing: t?.billing || null, day_rate: t?.day_rate ?? "",
      notes: t?.notes || "",
      pinned: !!t?.pinned_start, pinned_start: t?.pinned_start || "",
      predecessors: (t?.predecessors || []).map((p) => ({ task_id: p.task_id, link_type: p.link_type || "FS", lag: p.lag || 0 })),
    });
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps

  const composedName = !f ? "" : f.name_preset === "custom"
    ? f.name.trim()
    : f.name_preset ? `${f.name_preset}${f.name_detail.trim() ? ` - ${f.name_detail.trim()}` : ""}` : "";

  const savedRate = useMemo(() => {
    for (const name of f?.crew || []) {
      const o = crewOptions.find((x) => x.name === name && x.default_day_rate > 0);
      if (o) return o.default_day_rate;
    }
    return null;
  }, [f, crewOptions]);

  // Every task across every job, for cross-project links + previews.
  const allTasks = useMemo(() => {
    const m = {};
    for (const jb of jobs) for (const x of jb.tasks) m[x.id] = { ...x, _job: jb };
    return m;
  }, [jobs]);

  const descendants = useMemo(() => {
    if (!t) return new Set();
    const kids = {};
    for (const jb of jobs) for (const x of jb.tasks) for (const p of (x.predecessors || [])) (kids[p.task_id] = kids[p.task_id] || []).push(x.id);
    const out = new Set();
    const walk = (id) => { for (const k of kids[id] || []) if (!out.has(k)) { out.add(k); walk(k); } };
    walk(t.id);
    return out;
  }, [jobs, t]);
  const dependents = descendants.size;
  const affectedJobs = useMemo(() => {
    const set = new Set();
    for (const id of descendants) {
      const x = allTasks[id];
      if (x && x._job.id !== job?.id) set.add(x._job.job_short || x._job.job_number);
    }
    return [...set];
  }, [descendants, allTasks, job]);

  const pickedIds = new Set((f?.predecessors || []).map((p) => p.task_id));
  const optionGroups = useMemo(() => {
    if (!job) return [];
    const eligible = (x) => x.id !== t?.id && !descendants.has(x.id);
    const groups = [{ job: job, label: `${job.job_short || job.job_number || "This job"} — this job`, tasks: job.tasks.filter(eligible) }];
    for (const jb of jobs) {
      if (jb.id === job.id || jb.tasks.length === 0) continue;
      const ts = jb.tasks.filter(eligible);
      if (ts.length) groups.push({ job: jb, label: `${jb.job_short || jb.job_number} — ${jb.title || ""}`.trim(), tasks: ts });
    }
    return groups;
  }, [jobs, job, t, descendants]);

  const preview = useMemo(() => {
    if (!f || !job) return null;
    const blocked = new Set((job.delays || []).flatMap((d) => d.dates || []));
    const dur = Math.max(0.25, Number(f.duration) || 1);
    // Points are {d: Date, fr: fraction-of-day} so part-day tasks chain within one day.
    const later = (a, b) => (!a ? b : (b.d > a.d || (ymd(b.d) === ymd(a.d) && b.fr > a.fr)) ? b : a);
    let base = null;
    for (const p of f.predecessors) {
      const pt = allTasks[p.task_id];
      if (!pt) continue;
      const lag = Number(p.lag) || 0;
      let cand;
      if (p.link_type === "SS") {
        cand = { d: lag > 0 ? addWork(parse(pt.start), lag, blocked) : parse(pt.start), fr: pt.start_frac || 0 };
      } else if (p.link_type === "FF") {
        let d = lag > 0 ? addWork(parse(pt.finish), lag, blocked) : parse(pt.finish);
        let fr = pt.end_frac ?? 1, g = 0;
        while (!isWork(d, blocked) && g < 400) { d = addDays(d, -1); fr = 1; g++; }
        let rem = dur;
        while (g < 800 && rem > fr + 1e-9) {
          rem -= fr;
          d = addDays(d, -1);
          while (!isWork(d, blocked) && g < 800) { d = addDays(d, -1); g++; }
          fr = 1;
          g++;
        }
        cand = { d, fr: Math.max(0, fr - rem) };
      } else {
        cand = { d: lag > 0 ? addWork(parse(pt.finish), lag, blocked) : parse(pt.finish), fr: pt.end_frac ?? 1 };
      }
      base = later(base, cand);
    }
    if (f.pinned && f.pinned_start) base = later(base, { d: parse(f.pinned_start), fr: 0 });
    else if (base === null) base = { d: nextWork(parse(job.plan_anchor || job.start || ymd(new Date())), blocked), fr: 0 };
    // normalise then consume the duration forward
    let { d, fr } = base;
    if (!isWork(d, blocked)) { d = nextWork(d, blocked); fr = 0; }
    else if (fr >= 0.999) { d = nextWork(addDays(d, 1), blocked); fr = 0; }
    const start = d;
    let rem = dur, g = 0;
    while (g < 800) {
      const avail = 1 - fr;
      if (rem <= avail + 1e-9) break;
      rem -= avail;
      d = nextWork(addDays(d, 1), blocked);
      fr = 0;
      g++;
    }
    return { start: ymd(start), finish: ymd(d) };
  }, [f, job, allTasks]);

  const payload = () => ({
    name: composedName, duration: Math.max(0.25, Number(f.duration) || 1), percent: Number(f.percent) || 0,
    crew: f.crew, pinned_start: f.pinned && f.pinned_start ? f.pinned_start : null,
    billing: f.crew.length ? f.billing : null,
    day_rate: f.crew.length && f.billing === "mandays" ? Number(f.day_rate) || 0 : null,
    notes: f.notes.trim() || null,
    predecessors: f.predecessors.filter((p) => p.task_id).map((p) => ({ task_id: p.task_id, link_type: p.link_type, lag: Number(p.lag) || 0 })),
  });
  const save = useMutation({
    mutationFn: async () => t
      ? (await api.patch(`/project/tasks/${t.id}`, payload())).data
      : (await api.post(`/project/jobs/${job.id}/tasks`, payload())).data,
    onSuccess: () => { invalidateAll(qc); toast.success(t ? "Task updated — dependents rescheduled" : "Task added"); onClose(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save task"),
  });
  const del = useMutation({
    mutationFn: async () => (await api.delete(`/project/tasks/${t.id}`)).data,
    onSuccess: () => { invalidateAll(qc); toast.success("Task deleted"); onClose(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not delete task"),
  });

  if (!draft || !f || !job) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-xl max-h-[92vh] overflow-y-auto" data-testid="project-task-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            {t ? "Edit task" : "New task"}
            <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-cyan-300 bg-cyan-50 text-cyan-700">{job.job_number || job.title}</Badge>
            {!canEdit && <Badge variant="outline" className="rounded-sm text-[10px] border-slate-300 text-slate-500">VIEW ONLY</Badge>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="stamp-label text-slate-600">Task type</Label>
              <Select value={f.name_preset} onValueChange={(v) => setF({ ...f, name_preset: v })} disabled={!canEdit}>
                <SelectTrigger className="mt-1 rounded-sm h-9 text-sm" data-testid="task-name-preset">
                  <SelectValue placeholder="Pick a task type…" />
                </SelectTrigger>
                <SelectContent>
                  {TASK_PRESETS.map((p) => <SelectItem key={p} value={p} className="text-sm">{p}</SelectItem>)}
                  <SelectItem value="custom" className="text-sm">Other / Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {f.name_preset === "custom" ? (
              <div>
                <Label className="stamp-label text-slate-600">Custom name</Label>
                <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} disabled={!canEdit} className="mt-1 rounded-sm h-9" placeholder="Install posts & concrete" data-testid="task-name-input" />
              </div>
            ) : (
              <div>
                <Label className="stamp-label text-slate-600">Detail (optional)</Label>
                <Input value={f.name_detail} onChange={(e) => setF({ ...f, name_detail: e.target.value })} disabled={!canEdit} className="mt-1 rounded-sm h-9" placeholder="e.g. Posts" data-testid="task-name-detail" />
              </div>
            )}
          </div>
          {composedName && f.name_preset !== "custom" && f.name_detail.trim() && (
            <p className="text-[10.5px] text-slate-400 -mt-1.5">Shown as: <b className="text-slate-600">{composedName}</b></p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="stamp-label text-slate-600">Duration (days — ¼ steps)</Label>
              <div className="flex items-center gap-1.5 mt-1">
                <Input type="number" min={0.25} max={90} step={0.25} value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} disabled={!canEdit} className="rounded-sm h-9" data-testid="task-duration-input" />
                <div className="flex gap-0.5">
                  {[0.25, 0.5, 1].map((d) => (
                    <button key={d} type="button" disabled={!canEdit} onClick={() => setF({ ...f, duration: d })}
                      className={`px-1.5 h-9 rounded-sm text-[11px] font-bold border ${Number(f.duration) === d ? "bg-cyan-600 border-cyan-600 text-white" : "bg-white border-slate-300 text-slate-500 hover:border-cyan-400"}`}
                      data-testid={`task-duration-quick-${d}`}>
                      {d === 0.25 ? "¼" : d === 0.5 ? "½" : "1"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <Label className="stamp-label text-slate-600">% complete — {f.percent}%</Label>
              <input type="range" min={0} max={100} step={5} value={f.percent} onChange={(e) => setF({ ...f, percent: e.target.value })} disabled={!canEdit} className="mt-3 w-full accent-cyan-700" data-testid="task-percent-input" />
            </div>
          </div>
          <div>
            <Label className="stamp-label text-slate-600">Crew — contractors &amp; labour suppliers</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" disabled={!canEdit} className="mt-1 rounded-sm h-9 w-full justify-start font-normal text-sm" data-testid="task-crew-trigger">
                  {f.crew.length ? f.crew.join(", ") : <span className="text-slate-400">Pick crew / contractors…</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2 rounded-sm max-h-72 overflow-y-auto" align="start">
                {(crewOptions.length ? crewOptions : team).map((m) => {
                  const approvedOk = ["approved", "approved_legacy"].includes(m.approval_status);
                  const temp = !approvedOk && !!m.temp_approval;
                  const unapproved = m.approval_status && !approvedOk && !temp;
                  return (
                    <label key={m.id} className="flex items-center gap-2 py-1.5 px-1 text-sm cursor-pointer hover:bg-slate-50 rounded-sm">
                      <Checkbox checked={f.crew.includes(m.name)}
                        onCheckedChange={(v) => setF({ ...f, crew: v ? [...f.crew, m.name] : f.crew.filter((c) => c !== m.name) })}
                        data-testid={`task-crew-${m.id}`} />
                      <span className="flex-1 truncate">{m.name}</span>
                      {temp && <span className="text-[9px] font-bold text-amber-600 shrink-0">TEMP APPROVED</span>}
                      {unapproved && <span className="text-[9px] font-bold text-amber-600 shrink-0">NOT APPROVED</span>}
                    </label>
                  );
                })}
                {crewOptions.length === 0 && team.length === 0 && (
                  <div className="text-[11px] text-slate-400 italic px-1 py-1">No Contractors / Labour suppliers yet — add them on the Suppliers page.</div>
                )}
              </PopoverContent>
            </Popover>
          </div>
          {f.crew.length > 0 && (
            <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/70" data-testid="task-billing-panel">
              <Label className="stamp-label text-slate-600">Billing — how is this crew costed?</Label>
              <div className="flex gap-2 mt-1.5">
                <button type="button" disabled={!canEdit} data-testid="task-billing-po"
                  onClick={() => setF({ ...f, billing: "po" })}
                  className={`px-3 py-1.5 rounded-sm text-[11px] font-bold border ${f.billing === "po" ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-violet-400"}`}>
                  Purchase order
                </button>
                <button type="button" disabled={!canEdit} data-testid="task-billing-mandays"
                  onClick={() => setF({ ...f, billing: "mandays", day_rate: f.day_rate || savedRate || "" })}
                  className={`px-3 py-1.5 rounded-sm text-[11px] font-bold border ${f.billing === "mandays" ? "bg-cyan-600 border-cyan-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-cyan-400"}`}>
                  Man-days (day rate)
                </button>
              </div>
              {f.billing === "mandays" && (
                <>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-slate-600">Day rate $</span>
                    <Input type="number" min={0} step={50} value={f.day_rate}
                      onChange={(e) => setF({ ...f, day_rate: e.target.value })} disabled={!canEdit}
                      className="rounded-sm h-8 w-28" placeholder={savedRate ? String(savedRate) : "850"} data-testid="task-day-rate-input" />
                    <span className="text-xs text-slate-500 font-mono" data-testid="task-billing-cost">
                      × {Math.max(0.25, Number(f.duration) || 1)}d = <b className="text-slate-900">${((Number(f.day_rate) || 0) * Math.max(0.25, Number(f.duration) || 1)).toLocaleString()}</b>
                    </span>
                    {savedRate && Number(f.day_rate) !== savedRate && (
                      <button type="button" disabled={!canEdit} data-testid="task-use-saved-rate"
                        onClick={() => setF({ ...f, day_rate: savedRate })}
                        className="text-[10px] font-semibold text-cyan-700 underline underline-offset-2">
                        Use saved rate ${savedRate.toLocaleString()}
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1.5">Feeds the job budget automatically — recalculates whenever the task's days change. The rate is remembered for this crew next time.</p>
                </>
              )}
              {f.billing === "po" && (
                <p className="text-[10px] text-slate-400 mt-1.5">Exempt from budget tracking — this crew's cost is picked up through the job's purchase orders.</p>
              )}
              {!f.billing && (
                <p className="text-[10px] text-amber-600 mt-1.5" data-testid="task-billing-hint">Pick how this crew is billed so the job budget stays accurate.</p>
              )}
            </div>
          )}
          <div>
            <Label className="stamp-label text-slate-600">Notes (optional)</Label>
            <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} disabled={!canEdit}
              className="mt-1 rounded-sm text-sm" placeholder="Anything the crew should know for this step…" data-testid="task-notes-input" />
          </div>
          <div>
            <Label className="stamp-label text-slate-600">Scheduling</Label>
            <div className="mt-1.5 space-y-2 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={!f.pinned} onChange={() => setF({ ...f, pinned: false })} disabled={!canEdit} className="accent-cyan-700" data-testid="task-auto-radio" />
                <span><b>Auto</b> — starts when its predecessor finishes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer flex-wrap">
                <input type="radio" checked={f.pinned} onChange={() => setF({ ...f, pinned: true })} disabled={!canEdit} className="accent-cyan-700" data-testid="task-pinned-radio" />
                <span><b>Pinned</b> — start no earlier than</span>
                <Input type="date" value={f.pinned_start} onChange={(e) => setF({ ...f, pinned_start: e.target.value, pinned: true })} disabled={!canEdit} className="rounded-sm h-8 w-[150px]" data-testid="task-pinned-date" />
              </label>
            </div>
          </div>
          <div>
            <Label className="stamp-label text-slate-600">Predecessors — this task starts once ALL of them allow it</Label>
            <div className="mt-1.5 space-y-2" data-testid="task-pred-list">
              {f.predecessors.length === 0 && (
                <div className="text-[11px] text-slate-400 italic">None — starts at the job anchor{f.pinned ? " / pin date" : ""}.</div>
              )}
              {f.predecessors.map((p, pi) => {
                const pt = allTasks[p.task_id];
                return (
                  <div key={pi} className="grid grid-cols-[1fr_120px_60px_28px] gap-1.5 items-center">
                    <Select value={p.task_id} onValueChange={(v) => setF({ ...f, predecessors: f.predecessors.map((x, i) => i === pi ? { ...x, task_id: v } : x) })} disabled={!canEdit}>
                      <SelectTrigger className="rounded-sm h-9 text-xs" data-testid={`task-pred-select-${pi}`}>
                        <SelectValue placeholder="Pick a task…">
                          {pt ? <span className="truncate">{pt._job.id !== job.id && <span className="text-violet-600 font-semibold">{pt.short_id} </span>}{pt._job.id === job.id && <span className="text-slate-400">{pt.task_no} </span>}{pt.name}</span> : "Pick a task…"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {optionGroups.map((g) => (
                          <SelectGroup key={g.job.id}>
                            <SelectLabel className={`text-[10px] uppercase tracking-wider ${g.job.id === job.id ? "text-cyan-700" : "text-violet-600"}`}>{g.label}</SelectLabel>
                            {g.tasks.map((x) => (
                              <SelectItem key={x.id} value={x.id} disabled={pickedIds.has(x.id) && x.id !== p.task_id} className="text-xs">
                                {g.job.id === job.id ? `${x.task_no} — ${x.name}` : `${x.short_id} — ${x.name}`}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={p.link_type} onValueChange={(v) => setF({ ...f, predecessors: f.predecessors.map((x, i) => i === pi ? { ...x, link_type: v } : x) })} disabled={!canEdit}>
                      <SelectTrigger className="rounded-sm h-9 text-xs" data-testid={`task-linktype-select-${pi}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FS">Finish → Start</SelectItem>
                        <SelectItem value="SS">Start → Start</SelectItem>
                        <SelectItem value="FF">Finish → Finish</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="number" min={0} max={60} value={p.lag} title="Lag (working days)"
                      onChange={(e) => setF({ ...f, predecessors: f.predecessors.map((x, i) => i === pi ? { ...x, lag: e.target.value } : x) })}
                      disabled={!canEdit} className="rounded-sm h-9 text-xs" data-testid={`task-lag-input-${pi}`} />
                    {canEdit && (
                      <button type="button" onClick={() => setF({ ...f, predecessors: f.predecessors.filter((_, i) => i !== pi) })}
                        className="text-slate-400 hover:text-rose-600 flex justify-center" data-testid={`task-pred-remove-${pi}`}><X size={14} /></button>
                    )}
                  </div>
                );
              })}
              {canEdit && f.predecessors.length < 10 && (
                <button type="button" onClick={() => setF({ ...f, predecessors: [...f.predecessors, { task_id: "", link_type: "FS", lag: 0 }] })}
                  className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-cyan-700 hover:underline" data-testid="task-add-pred-btn">
                  <Link2 size={12} /> Add predecessor — this job or another
                </button>
              )}
            </div>
          </div>
          {preview && (
            <div className="bg-teal-50 border border-teal-200 rounded-sm px-3 py-2 text-xs text-teal-800" data-testid="task-preview">
              Scheduled: <b className="font-mono">{fmtD(preview.start)} → {fmtD(preview.finish)}</b> · skips weekends{(job.delays || []).length > 0 ? ` and ${job.delays.length} delay${job.delays.length > 1 ? "s" : ""}` : ""}
            </div>
          )}
          {canEdit && dependents > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-800" data-testid="task-dependents-warning">
              Saving reschedules <b>{dependents} dependent task{dependents > 1 ? "s" : ""}</b>
              {affectedJobs.length > 0 && <> — including in <b className="text-violet-700">{affectedJobs.join(", ")}</b> (their crews get notified)</>}
              {" "}and keeps job dates + crew calendars in sync.
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          {canEdit && t && (
            <Button variant="outline" onClick={() => del.mutate()} disabled={del.isPending} className="rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50 mr-auto" data-testid="delete-task-btn">
              <Trash2 size={13} className="mr-1" /> Delete
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" onClick={() => onAddDelay(job.id)} className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="task-add-delay-btn">
              <CloudRain size={13} className="mr-1" /> Add delay to job
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} className="rounded-sm">{canEdit ? "Cancel" : "Close"}</Button>
          {canEdit && (
            <Button onClick={() => save.mutate()} disabled={save.isPending || !composedName} className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="save-task-btn">
              {save.isPending ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Save task
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const DELAY_REASONS = ["Rain / weather", "No site access", "Materials late", "Crew unavailable", "Other"];

export function DelayDialog({ draft, jobs, canEdit, onClose }) {
  const qc = useQueryClient();
  const job = jobs.find((j) => j.id === draft?.jobId);
  const dl = draft?.delay || null;
  const [f, setF] = useState(null);
  useEffect(() => {
    if (!draft) { setF(null); return; }
    const today = ymd(new Date());
    const defStart = job?.start && (today < job.start || (job.finish && today > job.finish)) ? job.start : today;
    setF({ reason: dl?.reason || "Rain / weather", start: dl?.start || defStart, days: dl?.days || 1, note: dl?.note || "" });
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: async () => {
      const body = { reason: f.reason, start: f.start, days: Math.max(1, Number(f.days) || 1), note: f.note || null };
      return dl ? (await api.patch(`/project/delays/${dl.id}`, body)).data
                : (await api.post(`/project/jobs/${job.id}/delays`, body)).data;
    },
    onSuccess: () => { invalidateAll(qc); toast.success(dl ? "Delay updated — plan reshuffled" : "Delay added — tasks shifted past it"); onClose(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save delay"),
  });
  const del = useMutation({
    mutationFn: async () => (await api.delete(`/project/delays/${dl.id}`)).data,
    onSuccess: () => { invalidateAll(qc); toast.success("Delay removed — tasks pulled back in"); onClose(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not remove delay"),
  });

  if (!draft || !f || !job) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="project-delay-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <CloudRain size={16} className="text-amber-600" /> {dl ? "Edit delay" : "Add delay"}
            <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-amber-300 bg-amber-50 text-amber-700">{job.job_number || job.title}</Badge>
            {!canEdit && <Badge variant="outline" className="rounded-sm text-[10px] border-slate-300 text-slate-500">VIEW ONLY</Badge>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="stamp-label text-slate-600">Reason</Label>
            <Select value={f.reason} onValueChange={(v) => setF({ ...f, reason: v })} disabled={!canEdit}>
              <SelectTrigger className="mt-1 rounded-sm h-9" data-testid="delay-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DELAY_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="stamp-label text-slate-600">First day lost</Label>
              <Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} disabled={!canEdit} className="mt-1 rounded-sm h-9" data-testid="delay-start-input" />
            </div>
            <div>
              <Label className="stamp-label text-slate-600">Working days lost</Label>
              <Input type="number" min={1} max={30} value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} disabled={!canEdit} className="mt-1 rounded-sm h-9" data-testid="delay-days-input" />
            </div>
          </div>
          <div>
            <Label className="stamp-label text-slate-600">Note (optional)</Label>
            <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} disabled={!canEdit} className="mt-1 rounded-sm h-9" placeholder="Site flooded after storm…" data-testid="delay-note-input" />
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Every unfinished task on this job from the first day lost shifts past the delay (weekends skipped). Other jobs are not affected.
            The crew get a bell/push heads-up when the finish date moves.
          </p>
        </div>
        <DialogFooter className="gap-2">
          {canEdit && dl && (
            <Button variant="outline" onClick={() => del.mutate()} disabled={del.isPending} className="rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50 mr-auto" data-testid="delete-delay-btn">
              <Trash2 size={13} className="mr-1" /> Remove delay
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} className="rounded-sm">{canEdit ? "Cancel" : "Close"}</Button>
          {canEdit && (
            <Button onClick={() => save.mutate()} disabled={save.isPending} className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-delay-btn">
              {save.isPending ? <Loader2 size={14} className="animate-spin mr-1" /> : null} {dl ? "Save delay" : "Add delay"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
